import { environment } from "@app/relay/relay-environment";
import { useState } from "react";
import { commitMutation, graphql } from "react-relay";

import type { assignCell_AssignTxnStakeholderMutation } from "@app/queries/assignCell_AssignTxnStakeholderMutation.graphql";

const assignMutation = graphql`
  mutation assignCell_AssignTxnStakeholderMutation(
    $input: AssignTxnStakeholderInputObject!
  ) {
    assignTxnStakeholder(input: $input) {
      stakeholder {
        id
        name
      }
      errors {
        message
      }
    }
  }
`;

export type StakeholderOption = { readonly id: string; readonly name: string };

// Set / change / clear a transaction's counterparty: type-to-search over
// existing stakeholders, create one on the fly from the typed name, or —
// when editing an attributed row with the input cleared — remove the
// attribution. The parent owns which row's editor is open, so only one
// dropdown exists at a time.
export default function AssignCell({
  txnId,
  options,
  current,
  open,
  onOpenChange,
  onAssigned,
}: {
  txnId: string;
  options: ReadonlyArray<StakeholderOption>;
  current?: StakeholderOption;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAssigned: () => void;
}) {
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openEditor = () => {
    setQuery(current?.name ?? "");
    setError(null);
    onOpenChange(true);
  };

  const close = () => {
    setError(null);
    onOpenChange(false);
  };

  const assign = (input: { stakeholderId?: string; name?: string }) => {
    setSaving(true);
    setError(null);

    commitMutation<assignCell_AssignTxnStakeholderMutation>(environment, {
      mutation: assignMutation,
      variables: { input: { txnId, ...input } },
      onCompleted: (response) => {
        setSaving(false);
        const payload = response.assignTxnStakeholder;
        if (payload?.stakeholder) {
          close();
          onAssigned();
        } else {
          setError(payload?.errors?.[0]?.message ?? "Could not save");
        }
      },
      onError: (err) => {
        setSaving(false);
        setError(err.message);
      },
    });
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={openEditor}
        className="text-xs text-gray-300 hover:text-gray-600 underline decoration-dotted"
      >
        {current ? "edit" : "assign"}
      </button>
    );
  }

  const trimmed = query.trim();
  const matches = trimmed
    ? options
        .filter(
          (o) =>
            o.name.toLowerCase().includes(trimmed.toLowerCase()) &&
            o.id !== current?.id
        )
        .slice(0, 8)
    : [];

  return (
    <div className="relative">
      <span className="relative inline-block">
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Escape" && close()}
          placeholder="Type a counterparty..."
          disabled={saving}
          className="rounded border pl-2 pr-6 py-1 text-xs w-48"
        />
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-600 text-xs"
        >
          ✕
        </button>
      </span>

      {(trimmed || error || current) && (
        <div className="absolute z-10 mt-1 w-64 bg-white rounded border shadow-lg text-xs">
          {error && <p className="px-2 py-1.5 text-red-600">{error}</p>}
          {matches.map((option) => (
            <button
              key={option.id}
              type="button"
              disabled={saving}
              onClick={() => assign({ stakeholderId: option.id })}
              className="block w-full text-left px-2 py-1.5 hover:bg-gray-50"
            >
              {option.name}
            </button>
          ))}
          {trimmed && trimmed !== current?.name && (
            <button
              type="button"
              disabled={saving}
              onClick={() => assign({ name: trimmed })}
              className="block w-full text-left px-2 py-1.5 font-medium text-emerald-700 hover:bg-emerald-50 border-t"
            >
              {saving ? "Saving..." : `+ add "${trimmed}"`}
            </button>
          )}
          {current && !trimmed && (
            <button
              type="button"
              disabled={saving}
              onClick={() => assign({})}
              className="block w-full text-left px-2 py-1.5 font-medium text-red-700 hover:bg-red-50 border-t"
            >
              {saving ? "Saving..." : "✕ clear attribution"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
